import { SubscriptionType, UserTypeDBFormat } from '../../components/constants/securspace-constants';
import { validateEmail, validateName, validatePhoneNumber } from '../../util/ValidationUtils';
import { NewLocationManagerData, WIZARD_STEP_MANAGER } from './types';

/** Dropdown value of the "+ Add New Manager" option (never a real username). */
export const ADD_NEW_MANAGER_VALUE = '__add_new_manager__';

export const createEmptyNewLocationManager = (): NewLocationManagerData => ({
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  isOptedIntoText: false,
});

export type NewLocationManagerField = 'firstName' | 'lastName' | 'phoneNumber' | 'email';

/** Field -> message for each invalid or empty field of the inline new-manager form. */
export type NewLocationManagerErrors = Partial<Record<NewLocationManagerField, string>>;

export const getNewLocationManagerErrors = (manager: NewLocationManagerData): NewLocationManagerErrors => {
  const errors: NewLocationManagerErrors = {};

  if (!manager.firstName.trim()) {
    errors.firstName = 'Please enter the manager\u2019s first name.';
  } else if (!validateName(manager.firstName)) {
    errors.firstName = 'First name can contain only letters, numbers, spaces and hyphens.';
  }

  if (!manager.lastName.trim()) {
    errors.lastName = 'Please enter the manager\u2019s last name.';
  } else if (!validateName(manager.lastName)) {
    errors.lastName = 'Last name can contain only letters, numbers, spaces and hyphens.';
  }

  if (!manager.phoneNumber) {
    errors.phoneNumber = 'Please enter the manager\u2019s phone number.';
  } else if (!validatePhoneNumber(manager.phoneNumber, true)) {
    errors.phoneNumber = 'Please enter a valid 10-digit phone number.';
  }

  if (!manager.email.trim()) {
    errors.email = 'Please enter the manager\u2019s email address.';
  } else if (!validateEmail(manager.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  return errors;
};

/** True when no new manager is being added, or the form being filled in is complete and valid. */
export const isNewLocationManagerComplete = (manager: NewLocationManagerData | null): boolean =>
  !manager || Object.keys(getNewLocationManagerErrors(manager)).length === 0;

/** True when nothing has been typed yet, so there is no pending change worth saving or discarding. */
export const isNewLocationManagerBlank = (manager: NewLocationManagerData): boolean =>
  !manager.firstName.trim() && !manager.lastName.trim() && !manager.phoneNumber && !manager.email.trim();

/** Normalizes the typed email to the form the API stores: usernames are lowercased server-side. */
export const toNewLocationManagerUsername = (email: string): string => email.trim().toLowerCase();

/**
 * Whether the footer's Next and Save must be disabled: the Manager step is showing a
 * "+ Add New Manager" form that can't be turned into a user yet.
 */
export const isManagerFormBlockingFooter = (
  currentStep: number,
  manager: NewLocationManagerData | null
): boolean => currentStep === WIZARD_STEP_MANAGER && !isNewLocationManagerComplete(manager);

/**
 * Role given to a manager created from the wizard: the least-privileged role the API allows for
 * the account's plan. Marketplace-only suppliers can only create Owners (shown as
 * "Administrator"); GMS suppliers can create Gate Managers.
 */
export const getNewLocationManagerRole = (subscriptionType?: string): string =>
  subscriptionType === SubscriptionType.MARKETPLACE_ONLY
    ? UserTypeDBFormat.ROLE_USERTYPE_OWNER
    : UserTypeDBFormat.ROLE_USERTYPE_GATEMANAGER;
